import { inject, Injectable } from '@angular/core';
import {
  AVAILABLE_LANGUAGES,
  DEFAULT_LANGUAGE,
  onLocaleChangeInitPlugins,
} from '@config/languages';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

export interface Locale {
  lang: string;
  data: any;
}

export const LOCALIZATION_LOCAL_STORAGE_KEY = 'language';

@Injectable({
  providedIn: 'root',
})
/**
 * @author Carlos Duardo <charlieandroid55@gmail.com>
 */
export class LocaleService {
  private translate: TranslateService = inject(TranslateService);
  private destroyer$: Subject<any> = new Subject<any>();

  private initialized = false;
  private langIds: any = [];

  constructor() {
    const otherInstance = inject(LocaleService, {
      optional: true,
      skipSelf: true,
    });

    if (otherInstance)
      throw new Error('LocaleService should have only one instance.');
  }

  get currentLocale(): string {
    return (
      localStorage.getItem(LOCALIZATION_LOCAL_STORAGE_KEY) ??
      this.translate.getCurrentLang()
    );
  }

  /**
   * Initialize the location in the app, for this it takes into account whether the location has not been previously initialized.
   * It then subscribes to language changes using this.translate.onLangChange, After subscription,
   * it sets the default language and the specific language, and marks the initialization as completed.
   *
   * Note that if you invoke this method you must call "destroySubscription()" when necessary to avoid memory leaks.
   *
   * @param localeID
   * @param defaultLocaleID
   */
  initLocale(localeID: string | null = null, defaultLocaleID = DEFAULT_LANGUAGE) {
    if (this.initialized) return;

    localeID ??= this.currentLocale ?? defaultLocaleID;

    // Si el idioma resuelto (p.ej. uno guardado antes, como 'cat') ya no está
    // entre los disponibles, se cae al idioma por defecto (español) y se limpia
    // la clave obsoleta. Con el selector oculto, esto evita quedar en un idioma
    // no soportado sin forma de cambiarlo.
    const isSupported = AVAILABLE_LANGUAGES.some((l) => l.lang === localeID);
    if (!isSupported) {
      localStorage.removeItem(LOCALIZATION_LOCAL_STORAGE_KEY);
      localeID = defaultLocaleID;
    }

    this.translate.onLangChange
      .pipe(takeUntil(this.destroyer$))
      .subscribe((event) => {
        this.initPlugins(event.lang);
      });

    this.setDefaultLocale(defaultLocaleID);
    this.setLocale(localeID);

    this.initialized = true;
  }

  setDefaultLocale(localeID: string) {
    this.translate.setFallbackLang(localeID);
  }

  setLocale(localeID: string) {
    localStorage.setItem(LOCALIZATION_LOCAL_STORAGE_KEY, localeID);
    this.translate.use(localeID);
  }

  loadLocalesTranslations(...args: Locale[]): void {
    [...args].forEach((locale) => {
      this.loadLocaleTranslation(locale);
    });
  }

  loadLocaleTranslation(locale: Locale) {
    if (!this.translate.getLangs().includes(locale.lang)) {
      // use setTranslation() with the third argument set to true
      // to append translations instead of replacing them
      this.translate.setTranslation(locale.lang, locale.data, false);

      this.langIds.push(locale.lang);
    }
  }

  // lazyLoadLocaleTranslationFromLangID(localeID: string) {
  //   const vocabs = I18N_AVAILABLE_VOCABS.filter((vocab: ILangVocab) => vocab.lang === localeID);
  //   this.loadLocalesTranslations(...vocabs)
  // }

  private initPlugins(localeID: string) {
    onLocaleChangeInitPlugins(localeID);
  }

  destroySubscription() {
    this.destroyer$.next(null);
    this.destroyer$.complete();
  }
}
